import { Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import { memo, useCallback, useMemo } from 'react';

import { ConfigurationsPageFormValues } from '../../../../pages/ConfigurationPage';
import { isAirlineConfiguration } from '../../../../pages/ConfigurationPage.utils';
import { StyledMuiTextField } from '../../../../Resources/ResourceContent/ResourceFields/ResourceFieldsLine/LineContainer/ResourceFieldsLineProperties.style';

export const ConfigurationFieldsLineAirlines = memo(function ConfigurationFieldsLineAirlines({
    index,
    arrayReplace,
}: {
    index: number;
    arrayReplace: (index: number, value: any) => void;
}) {
    const { errors, values, initialValues } = useFormikContext<ConfigurationsPageFormValues>();

    const handleChange = useCallback(
        (prop: string, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            arrayReplace(index, {
                ...values.configurations[index],
                content: {
                    ...values.configurations[index].content,
                    [prop]: event.target.value,
                },
            });
        },
        [arrayReplace, index, values.configurations],
    );

    const handleNumberChange = useCallback(
        (prop: string, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            arrayReplace(index, {
                ...values.configurations[index],
                content: {
                    ...values.configurations[index].content,
                    [prop]: Number(event.target.value),
                },
            });
        },
        [arrayReplace, index, values.configurations],
    );

    const content = useMemo(() => values.configurations[index].content, [index, values.configurations]);
    const initialContent = useMemo(
        () => (values.configurations[index].uuid ? initialValues.configurations[index].content : null),
        [index, initialValues.configurations, values.configurations],
    );

    if (!isAirlineConfiguration(content)) {
        return null;
    }

    return (
        <>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    value={content.name}
                    modified={
                        values.configurations[index].uuid && isAirlineConfiguration(initialContent)
                            ? content.name !== initialContent.name
                            : undefined
                    }
                    error={!!errors[`configurations[${index}]['name']`]}
                    helperText={errors[`configurations[${index}]['name']`]}
                    onChange={e => handleChange('name', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    value={content.codeIata}
                    modified={
                        values.configurations[index].uuid && isAirlineConfiguration(initialContent)
                            ? content.codeIata !== initialContent.codeIata
                            : undefined
                    }
                    error={!!errors[`configurations[${index}]['codeIata']`]}
                    helperText={errors[`configurations[${index}]['codeIata']`]}
                    onChange={e => handleChange('codeIata', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    value={content.codeIcao}
                    modified={
                        values.configurations[index].uuid && isAirlineConfiguration(initialContent)
                            ? content.codeIcao !== initialContent.codeIcao
                            : undefined
                    }
                    error={!!errors[`configurations[${index}]['codeIcao']`]}
                    helperText={errors[`configurations[${index}]['codeIcao']`]}
                    onChange={e => handleChange('codeIcao', e)}
                />
            </Grid>

            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={content.gateOpeningOffset}
                    modified={
                        values.configurations[index].uuid && isAirlineConfiguration(initialContent)
                            ? content.gateOpeningOffset !== initialContent.gateOpeningOffset
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0 },
                    }}
                    error={!!errors[`configurations[${index}]['gateOpeningOffset']`]}
                    helperText={errors[`configurations[${index}]['gateOpeningOffset']`]}
                    onChange={e => handleNumberChange('gateOpeningOffset', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={content.gateClosingOffset}
                    modified={
                        values.configurations[index].uuid && isAirlineConfiguration(initialContent)
                            ? content.gateClosingOffset !== initialContent.gateClosingOffset
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0 },
                    }}
                    error={!!errors[`configurations[${index}]['gateClosingOffset']`]}
                    helperText={errors[`configurations[${index}]['gateClosingOffset']`]}
                    onChange={e => handleNumberChange('gateClosingOffset', e)}
                />
            </Grid>
        </>
    );
});
